/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  getUomQuantityType,
  HousePlanFinderListItem,
  PbComponentItemFinderListItem,
  PbHousePlanFinderListItem,
  SalesTaxRateListItem,
} from '@gh/builder-shared';
import {
  ContactDeliveryMethod,
  createUomQuantity,
  GtoUom,
  HousePlanDetailsDto,
  HousePlanTopListCostCenterDto,
  HousePlanTopListDto,
  HousePlanTopListItemDto,
  QuotationApproveDto,
  QuotationBase,
  QuotationCloneRequestDto,
  QuotationCloneVariationsDto,
  QuotationDetailsInitResponseDto,
  QuotationDetailsSaveRequestDto,
  QuotationListFilterDto,
  QuotationListItemDto,
  QuotationPrintRequestDto,
  QuotationPrintResponseDto,
  QuotationVariationInitResponseDto,
  QuotationVariationsIdsGetResponseDto,
  QuoteSalesTaxRateDto,
  SalesFolderShortDto,
  SalesFolderStatus,
  SiteListItemDto,
  TOP_CODE_CONTRACT_PRICE,
  UomQuantityMapper,
} from '@gh/builder-shared-data';
import {
  createOrderIndexDriver,
  createTableDriver,
  EntityConfig,
  entityStateMerger,
  entityValidatorByEntityConfig,
  errorsEntityState,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  newEntityComparator,
  nextNewItemId,
  NumberTypes,
  TextTypes,
  whenExisting,
} from '@gh/common-shared';
import {
  AutocompleteItem,
  DirtyType,
  ID,
  WithCreatedDate,
  WithDirtyType,
  WithEntityState,
  WithVersion,
} from '@gh/common-shared-data';
import { Quantity } from '@gh/core-data';
import { composeComparators, isNotNil, negateComparator, valueComparator } from '@gh/core-util';
import { defaults, omit, pick, pickBy, stubTrue } from 'lodash';

export enum QuoteVariationType { Normal, Total }

export type QuoteHpFinderListItem = PbHousePlanFinderListItem;
export type QuoteCloneRequest = QuotationCloneRequestDto;
export type QuoteCloneVariationsRequest = QuotationCloneVariationsDto;
export type QuoteSalesTaxRate = QuoteSalesTaxRateDto;
export type QuoteVariation = WithCreatedDate<WithEntityState<WithDirtyType<QuotationVariationInitResponseDto & {
  variationId: ID;
  type: QuoteVariationType.Normal;
}>>>;
export type QuoteVariationTotal = {
  variationId: string;
  credit?: number;
  debit?: number;
  profit?: number;
  priceWithGst?: number;
  totalPriceWithGst?: number;
  type: QuoteVariationType.Total;
};
export type QuoteVariationOrTotal = QuoteVariation | QuoteVariationTotal;
export type QuoteDetailsInitResponse = WithVersion<{
  contactId: ID;
  quotationId: ID;
  masterInfo: QuoteDetailsMasterInfo;
  bottomInfo: QuoteDetailsBottomInfo;
  derivedInfo: QuoteDetailsDerivedInfo;
  variations: QuoteVariation[];
  isSalesEstimateShown: boolean;
  isQuickQuotesAllowed: boolean;
  isGtoTopExist: boolean;
  useSalesEstimate: boolean;
}>;
export type QuoteDetailsSaveRequest = QuotationDetailsSaveRequestDto;
export type QuoteDetailsSaveResponse = QuoteDetailsInitResponse;
export type SiteListItem = SiteListItemDto;
export type QuoteVariationsIdsGetResponse = QuotationVariationsIdsGetResponseDto;

export type CloneSalesFolder = SalesFolderShortDto;
export type CloneSite = SiteListItemDto;
export type CloneQuote = QuotationListItemDto;

export type QuoteHousePlan = HousePlanDetailsDto;
export type QuoteHousePlanTopListCc = HousePlanTopListCostCenterDto;
export type QuoteHousePlanTop = WithEntityState<WithDirtyType<{
  topId: ID;
  code: string;
  uomCode: GtoUom;
  value: Quantity<any>;
  isFromGto: boolean;
  baseValue?: Quantity<any>;
}>>;
export type QuoteHousePlanTopList = {
  priceBookId: ID;
  housePlanCode: string;
  costCenters: QuoteHousePlanTopListCc[];
  takeOffParams: QuoteHousePlanTop[];
};

export type QuoteDetailsMasterInfo = {
  description: string;
  priceBook?: AutocompleteItem;
  base: QuotationBase;
  site?: SiteListItem;
  baseHousePlan?: HousePlanFinderListItem;
  housePlan?: HousePlanFinderListItem;
  salesTaxRates: QuoteSalesTaxRate[];
  customerSfPlansFolderId: ID;
  isRounded: boolean;
};

export type QuoteDetailsDerivedInfo = {
  code: number;
  contactId: ID;
  customerName: string;
  email?: string;
  deliveryBy: ContactDeliveryMethod;
  salesFolderId: ID;
  salesFolderName: string;
  salesFolderStatus: SalesFolderStatus;
  rfqNumber?: number;
  jobNumber?: number;
  created: Date;
  approvedDate?: Date;
  approvedByName?: string;

  pbSalesPrice: number; // PRICE_1
  pbPrice: number; // PRICE_2

  debit: number; // DEBIT_TOTAL
  credit: number; // CREDIT_TOTAL
  profit: number; // PROFIT_TOTAL
  totalPriceWithGst: number; // CUSTOMER_PRICE_TOTAL

  rfqPrice1: number; // QUOTE_TOTAL_1
  rfqPrice2: number; // QUOTE_TOTAL_2
  pbCost: number;

  origSalesTax: number;
  adjSalesTax: number;
  totalSalesTax: number;

  isCompletionQuote: boolean;
};

export type QuoteDetailsBottomInfo = {
  memo: string;
  isMemoOnTop: boolean;
};

export type QuoteApprove = QuotationApproveDto;

export type QuotePrintFilter = {
  isAuthorityToProceedRequired: boolean;
  isPrintTakeOff: boolean;
  isShowBasePrice: boolean;
  isForEstimator: boolean;
  deliveryBy: ContactDeliveryMethod;
};
export type QuotePrintRequest = QuotationPrintRequestDto;
export type QuotePrintResponse = QuotationPrintResponseDto;

export type QuoteListItem = {
  addedDate: string;
  customerName: string;
  isApproved: boolean;
  updatedDate: string;
  quoatationId: ID;
  quotationName: string;
  rfqNumber: string;
  rfqPrice: number;
  salesFolderId: ID;
  salesFolderName: string;
  salespersonName: string;
  siteName: string;
};

export type QuoteListFilter = QuotationListFilterDto;

export function isQuoteVariationTotal(variation: QuoteVariationOrTotal): variation is QuoteVariationTotal {
  return variation.type === QuoteVariationType.Total;
}

export function isQuoteVariation(variation: QuoteVariationOrTotal): variation is QuoteVariation {
  return variation.type === QuoteVariationType.Normal;
}

export const getCloneSalesFolderId = (sf: CloneSalesFolder) => sf.salesFolderId;
export const getCloneSiteId = (site: CloneSite) => site.id;
export const getCloneQuoteId = (quote: CloneQuote) => quote.id;
export const getQuoteSalesTaxRateId = (str: QuoteSalesTaxRate) => str.salesTaxRateId;
export const getQuoteVariationId = (variation: QuoteVariation) => variation.variationId;

export const getQuoteHousePlanTopId = (top: QuoteHousePlanTop) => top.topId;

export const quoteVariationComparator = composeComparators<QuoteVariation>(
  newEntityComparator,
  valueComparator('itemCode'),
  valueComparator(getQuoteVariationId));
export const cloneSalesFolderComparator = composeComparators(
  negateComparator(valueComparator('created')),
  valueComparator(getCloneSalesFolderId));
export const cloneSiteComparator = composeComparators(
  negateComparator(valueComparator('inspectedDate')),
  valueComparator(getCloneSiteId));
export const cloneQuoteComparator = composeComparators(
  negateComparator(valueComparator('code')),
  valueComparator(getCloneQuoteId));

const generateVariationId = (quotationItemId: ID, isComponent: boolean) =>
  isComponent ? `component:${quotationItemId}` : `item:${quotationItemId}`;

export const quoteVariationFromDto = (dto: QuotationVariationInitResponseDto) => <QuoteVariation>({
  type: QuoteVariationType.Normal,
  ...dto,
  variationId: generateVariationId(dto.quotationItemId, dto.isComponent),
});

export const quoteVariationFromFinder = (quotationItemId: ID, item: PbComponentItemFinderListItem) => <QuoteVariation>({
  type: QuoteVariationType.Normal,
  variationId: generateVariationId(quotationItemId, item.isComponent),
  isComponent: item.isComponent,
  costCenterId: item.costCenterId,
  costCenterCode: item.costCenterCode,
  description: item.name,
  isCredit: item.price < 0,
  isInPriceBook: true,
  itemCode: item.code,
  itemId: item.itemId,
  itemPrice: item.price,
  price: 0,
  priceWithGst: 0,
  memo: item.memo,
  quantity: 1,
  origQuantity: item.origQuantity,
  profit: void 0,
  uomCode: item.uomCode,
  quotationItemId,
  dirtyType: DirtyType.New,
  newItemId: nextNewItemId(),
  grayOut: false,
});

export const quoteVariationsFromFinder = ({ componentIds, itemIds }: QuoteVariationsIdsGetResponse,
                                          structures: PbComponentItemFinderListItem[]) => {
  const components = structures.filter((structure) => structure.isComponent)
    .map((item, i) => quoteVariationFromFinder(componentIds[i], item));
  const items = structures.filter((struture) => !struture.isComponent)
    .map((item, i) => quoteVariationFromFinder(itemIds[i], item));

  return components.concat(items);
};

export const setVariationId = (variation: QuoteVariation, quotationItemId: ID) => ({
  ...variation,
  variationId: generateVariationId(quotationItemId, variation.isComponent),
  quotationItemId,
});

export const useZeroPricesForHp = (hp: QuoteHpFinderListItem) => ({
  ...hp,
  calcPrice: 0,
  salesPrice: 0,
});

export const quoteDetailsDerivedInfoFromDto = (dto: QuotationDetailsInitResponseDto) =>
  <QuoteDetailsDerivedInfo><any>defaults(
    pickBy(
      pick(
        dto,
        ['code', 'contactId', 'customerName', 'salesFolderId', 'salesFolderName', 'rfqNumber', 'created', 'email',
          'approvedDate', 'approvedByName', 'pbSalesPrice', 'pbPrice', 'debit', 'credit', 'profit', 'rfqPrice1',
          'rfqPrice2', 'pbCost', 'origSalesTax', 'adjSalesTax', 'totalSalesTax', 'salesFolderStatus', 'deliveryBy',
          'jobNumber', 'isCompletionQuote', 'totalPriceWithGst',
        ]),
      isNotNil),
    {
      pbSalesPrice: 0,
      pbPrice: 0,
      debit: 0,
      credit: 0,
      totalPriceWithGst: 0,
      profit: 0,
      rfqPrice1: 0,
      rfqPrice2: 0,
      pbCost: 0,
      origSalesTax: 0,
      adjSalesTax: 0,
      totalSalesTax: 0,
    },
  );

export const quoteDetailsInitFromDto = (dto: QuotationDetailsInitResponseDto) => <QuoteDetailsInitResponse>({
  contactId: dto.contactId,
  quotationId: dto.quotationId,
  masterInfo: {
    description: dto.description,
    priceBook: dto.priceBookId ? { id: dto.priceBookId, name: dto.priceBookName } : void 0,
    base: dto.base,
    isRounded: dto.isRounded,
    site: dto.siteId ? <SiteListItem>{ id: dto.siteId, name: dto.siteName } : void 0,
    baseHousePlan: dto.baseHousePlanId
      ? <HousePlanFinderListItem>{
        housePlanId: dto.baseHousePlanId,
        code: dto.baseHousePlanCode,
        name: dto.baseHousePlanName,
      }
      : void 0,
    housePlan: dto.housePlanId ?
      <HousePlanFinderListItem>{
        housePlanId: dto.housePlanId,
        code: dto.housePlanCode,
        name: dto.housePlanName,
      } : void 0,
    salesTaxRates: dto.salesTaxRates,
    pbSalesPrice: dto.pbSalesPrice,
    pbPrice: dto.pbPrice,
    customerSfPlansFolderId: dto.customerSfPlansFolderId,
  },
  bottomInfo: { memo: dto.memo, isMemoOnTop: dto.isMemoOnTop },
  derivedInfo: quoteDetailsDerivedInfoFromDto(dto),
  variations: (dto.variations || []).map(quoteVariationFromDto),
  isSalesEstimateShown: dto.isSalesEstimateShown,
  isQuickQuotesAllowed: dto.isQuickQuotesAllowed,
  isGtoTopExist: dto.isGtoTopExist,
  useSalesEstimate: dto.useSalesEstimate,
  version: dto.version,
});
export const quoteDetailsSaveToDto = (model: QuoteDetailsSaveRequest) => <QuotationDetailsSaveRequestDto>model;

export const quoteSalesTaxRateToFinder = (item: QuoteSalesTaxRate) => <SalesTaxRateListItem>({
  id: item.salesTaxRateId,
  name: '',
  rate: item.ratePct,
  isDefault: false,
});

export const quoteSalesTaxRateFromFinder = (item: SalesTaxRateListItem) => <QuoteSalesTaxRate>({
  salesTaxRateId: item.id,
  ratePct: item.rate,
});

export const quoteTotalFromDerivedInfo = ({ profit, credit, debit, totalPriceWithGst }: QuoteDetailsDerivedInfo,
                                          isProfitVisible: boolean) => <QuoteVariationTotal>({
  variationId: 'variation:footer',
  type: QuoteVariationType.Total,
  debit,
  credit,
  totalPriceWithGst,
  profit: isProfitVisible ? profit : void 0,
});

const housePlanTopListItemFromDto = (dto: HousePlanTopListItemDto) => <QuoteHousePlanTop>({
  ...omit(dto, 'value'),
  value: createUomQuantity(dto.value, dto.uomCode),
  baseValue: createUomQuantity(dto.value, dto.uomCode),
});

const housePlanTopListItemToDto = (model: QuoteHousePlanTop) =>
  <HousePlanTopListItemDto>{
    ...omit(model, ['baseValue', 'dirtyType', 'uomCode', 'code', 'entityState', 'value']),
    value: UomQuantityMapper.serialize(model.value),
  };

export const housePlanTopListFromDto = (dto: HousePlanTopListDto) => <QuoteHousePlanTopList>({
  priceBookId: dto.priceBookId,
  housePlanCode: dto.housePlanCode,
  costCenters: dto.costCenters,
  takeOffParams: (dto.takeOffParams || []).map(housePlanTopListItemFromDto),
});

export const housePlanTopListToDto = (model: QuoteHousePlanTopList) => <HousePlanTopListDto>({
  priceBookId: model.priceBookId,
  costCenters: model.costCenters,
  takeOffParams: model.takeOffParams.map(housePlanTopListItemToDto),
});

export const quoteVariationTable = createTableDriver<QuoteVariation>(
  getQuoteVariationId,
  createOrderIndexDriver('existing', getQuoteVariationId, whenExisting, quoteVariationComparator));

export const quoteHousePlanTopTable = createTableDriver<QuoteHousePlanTop>(
  getQuoteHousePlanTopId,
  createOrderIndexDriver('code', getQuoteHousePlanTopId, stubTrue, valueComparator(({ code }) => code.toLowerCase())));

export const getQuoteHpTopQuantityType = (top: QuoteHousePlanTop) => getUomQuantityType(top.uomCode);

const quoteVariationQuantityValidator = ({ isCredit, quantity, origQuantity, itemPrice }: QuoteVariation,
                                         field: string,
                                         value: any) => {
  if (isCredit && value > origQuantity && itemPrice > 0 && origQuantity > 0) {
    return { 'quantity': { messageId: 'lbl_reduce_quantity' } };
  }
};

const QUOTE_VARIATION_ENTITY_CONFIG: EntityConfig<QuoteVariation> = {
  description: {
    editable: isQuoteVariation,
    required: true,
    type: TextTypes.Text,
  },
  quantity: {
    editable: isQuoteVariation,
    required: true,
    type: NumberTypes.SignedQuantity,
    validator: quoteVariationQuantityValidator,
  },
};

const QUOTE_MASTER_INFO_ENTITY_CONFIG_FOR_CREATE: EntityConfig<QuoteDetailsMasterInfo> = {
  description: { required: true },
  priceBook: { required: true },
  base: { required: true },
  baseHousePlan: { required: true },
  site: { required: true },
};

const QUOTE_MASTER_INFO_ENTITY_CONFIG_FOR_EDIT: EntityConfig<QuoteDetailsMasterInfo> = {
  description: { required: true },
  site: { required: true },
};

const QUOTE_HOUSE_PLAN_TOP_ENTITY_CONFIG: EntityConfig<QuoteHousePlanTop> = {
  value: { required: true, type: getQuoteHpTopQuantityType, editable: (top) => top.code !== TOP_CODE_CONTRACT_PRICE },
};

export const quoteVariationFieldState = fieldStateByEntityConfig(QUOTE_VARIATION_ENTITY_CONFIG);
export const validateQuoteVariationField = fieldValidatorByEntityConfig(QUOTE_VARIATION_ENTITY_CONFIG);
export const validateQuoteVariation = entityValidatorByEntityConfig(QUOTE_VARIATION_ENTITY_CONFIG);
export const complementQuoteVariation = entityStateMerger(errorsEntityState(validateQuoteVariation));

// tslint:disable-next-line:max-line-length
export const validateQuoteMasterInfoForCreate = entityValidatorByEntityConfig(QUOTE_MASTER_INFO_ENTITY_CONFIG_FOR_CREATE);
export const validateQuoteMasterInfoForEdit = entityValidatorByEntityConfig(QUOTE_MASTER_INFO_ENTITY_CONFIG_FOR_EDIT);

export const quoteHousePlanTopFieldState = fieldStateByEntityConfig(QUOTE_HOUSE_PLAN_TOP_ENTITY_CONFIG);
export const validateQuoteHousePlanTopField = fieldValidatorByEntityConfig(QUOTE_HOUSE_PLAN_TOP_ENTITY_CONFIG);
